import React from 'react';
import {
	Grid,
	TextField,
	Box,
	Typography,
	InputAdornment,
} from '@mui/material';
import { ProductInput } from '../../gql/graphql';

interface PricingTaxTabProps {
	productData: Partial<ProductInput>;
	updateProductData: (updates: Partial<ProductInput>) => void;
}

function PricingTaxTab({ productData, updateProductData }: PricingTaxTabProps) {
	const handleChange = (field: keyof ProductInput) => (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const value = event.target.type === 'number' ? parseFloat(event.target.value) : event.target.value;
		updateProductData({ [field]: value });
	};

	const calculateProfit = () => {
		const price = productData.price || 0;
		const costPrice = productData.costPrice || 0;
		const profit = price - costPrice;
		const margin = costPrice > 0 ? ((profit / price) * 100).toFixed(2) : '0';
		return { profit, margin };
	};

	const { profit, margin } = calculateProfit();

	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Pricing & Tax Information
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Set pricing, costs, and tax details for this product
			</Typography>

			<Grid container spacing={3}>
				{/* Selling Price */}
				<Grid item xs={12} md={4}>
					<TextField
						fullWidth
						required
						label="Selling Price"
						name="price"
						type="number"
						value={productData.price || ''}
						onChange={handleChange('price')}
						InputProps={{
							startAdornment: <InputAdornment position="start">₹</InputAdornment>,
						}}
						helperText="Price at which you sell to customers"
					/>
				</Grid>

				{/* Cost Price */}
				<Grid item xs={12} md={4}>
					<TextField
						fullWidth
						label="Cost Price"
						name="costPrice"
						type="number"
						value={productData.costPrice || ''}
						onChange={handleChange('costPrice')}
						InputProps={{
							startAdornment: <InputAdornment position="start">₹</InputAdornment>,
						}}
						helperText="Your cost to acquire/produce this product"
					/>
				</Grid>

				{/* Tax Percentage */}
				<Grid item xs={12} md={4}>
					<TextField
						fullWidth
						label="Tax Percentage"
						name="taxPercentage"
						type="number"
						value={productData.taxPercentage || 0}
						onChange={handleChange('taxPercentage')}
						InputProps={{
							endAdornment: <InputAdornment position="end">%</InputAdornment>,
						}}
						helperText="GST or applicable tax rate"
					/>
				</Grid>

				{/* Commission Percentage */}
				<Grid item xs={12} md={4}>
					<TextField
						fullWidth
						label="Commission Percentage"
						name="commissionPercentage"
						type="number"
						value={productData.commissionPercentage || 0}
						onChange={handleChange('commissionPercentage')}
						InputProps={{
							endAdornment: <InputAdornment position="end">%</InputAdornment>,
						}}
						helperText="Percentage commission for sales staff"
					/>
				</Grid>

				{/* Profit Margin Info */}
				{productData.price && productData.costPrice && (
					<Grid item xs={12}>
						<Box
							sx={{
								p: 2,
								bgcolor: profit >= 0 ? 'success.lighter' : 'error.lighter',
								borderRadius: 1,
							}}
						>
							<Typography variant="subtitle2" gutterBottom>
								Profit Analysis
							</Typography>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<Typography variant="body2" color="text.secondary">
										Profit per Unit
									</Typography>
									<Typography variant="h6" color={profit >= 0 ? 'success.main' : 'error.main'}>
										₹{profit.toFixed(2)}
									</Typography>
								</Grid>
								<Grid item xs={12} sm={6}>
									<Typography variant="body2" color="text.secondary">
										Profit Margin
									</Typography>
									<Typography variant="h6" color={profit >= 0 ? 'success.main' : 'error.main'}>
										{margin}%
									</Typography>
								</Grid>
							</Grid>
						</Box>
					</Grid>
				)}

				<Grid item xs={12}>
					<Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
						Order Quantity Settings
					</Typography>
				</Grid>

				{/* Min Order Quantity */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						label="Minimum Order Quantity"
						name="minOrderQuantity"
						type="number"
						value={productData.minOrderQuantity || 1}
						onChange={handleChange('minOrderQuantity')}
						helperText="Minimum units customer can order"
					/>
				</Grid>

				{/* Max Order Quantity */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						label="Maximum Order Quantity"
						name="maxOrderQuantity"
						type="number"
						value={productData.maxOrderQuantity || ''}
						onChange={handleChange('maxOrderQuantity')}
						helperText="Maximum units customer can order (optional)"
					/>
				</Grid>
			</Grid>
		</Box>
	);
}

export default PricingTaxTab;
